import React from 'react';
import { createPortal } from 'react-dom';
import { useHoverPanel } from '@/hooks/useHoverPanel';
import { glyphFor } from '@/utils/railGlyph';

/**
 * RAIL BOX — DP60 Part 0d.
 *
 * The collapsed rail is a single column of identical square boxes: one size,
 * one border, one spacing, no exceptions. A box holds either two letters or the
 * destination's icon, decided by the tenant setting (see utils/railGlyph.js).
 *
 * The hover popup is the FixedBottomNav / Admin square mechanism, via
 * hooks/useHoverPanel — not a third implementation.
 *
 * DP104 Part 2 — THE TIP IS PORTALLED OUT OF THE RAIL.
 *
 * It was `position: absolute; z-index: 60` inside `.km-rail`, which is
 * `position: relative; z-index: 3000`. A z-index inside a stacking context
 * cannot lift anything out of it, so the tip could never rise above the drawer
 * (4000) or AI Chad (7000) — it drew behind whatever the page had on top, which
 * is exactly what Chad sees. Same fault, same fix as the Yard's tile preview
 * tooltip (components/worktable/WorkTableItem.js:777) and BlockQuickMenu
 * (BlockQuickMenu.js:101): `createPortal` to the body, `position: fixed`,
 * coordinates converted from the trigger's own rect and clamped to the
 * viewport, and the layer token — `--km-z-tooltip` — instead of a bare 60.
 */
export const RailBox = ({
  id,
  label,
  mode = 'letters',
  icon = null,
  active = false,
  darkMode = false,
  onClick,
  testId,
  glyph: glyphOverride,
  children = null,
  ariaLabel = null,
}) => {
  const hover = useHoverPanel();
  const btnRef = React.useRef(null);
  const [at, setAt] = React.useState(null);
  // glyph={null} for boxes that are not destinations (the tracker, the collapse
  // control): they carry their icon and no letters, so no misleading attribute.
  // null => draw the icon. Only the seven fixed rows have letters.
  const glyph = glyphOverride === null ? null : (glyphOverride || glyphFor(id));

  // The rect is read when the hover opens, not on every render: a portalled tip
  // has no layout relationship to its trigger any more, so the position has to
  // be measured. Clamped like BlockQuickMenu's, so a tip at the foot of the
  // rail cannot leave the viewport.
  React.useEffect(() => {
    if (!hover.expanded) return;
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const w = Math.min(240, Math.max(80, (label || '').length * 8 + 24));
    setAt({
      left: Math.max(8, Math.min(r.right + 14, window.innerWidth - w - 8)),
      top: Math.max(8, Math.min(r.top + r.height / 2 - 14, window.innerHeight - 40)),
    });
  }, [hover.expanded, label]);

  const tip = at && createPortal(
    <span
      data-menu-phase={hover.dataPhase}
      data-testid={`rail-box-tip-${id}`}
      style={{ position: 'fixed', left: at.left, top: at.top, zIndex: 'var(--km-z-tooltip)' }}
      className={`km-railbox__tip ${
        hover.expanded
          ? 'opacity-100 translate-x-0 pointer-events-auto duration-[120ms]'
          : hover.phase === 'fading'
            ? 'opacity-0 translate-x-0 pointer-events-auto duration-700'
            : 'opacity-0 -translate-x-1 pointer-events-none duration-300'
      } ${darkMode ? 'km-railbox__tip--dark' : ''}`}
    >
      {label}
    </span>,
    document.body,
  );

  return (
    <div className="km-railbox__wrap relative flex justify-center">
      <button
        ref={btnRef}
        type="button"
        onClick={onClick}
        {...hover.triggerProps}
        aria-label={ariaLabel || label}
        data-testid={testId || `rail-box-${id}`}
        data-rail-glyph={mode === 'letters' && glyph ? glyph : undefined}
        data-km-active={active ? 'true' : undefined}
        className={`km-railbox ${active ? 'km-railbox--active' : ''}`}
      >
        {children || ((mode === 'icons' || !glyph) && icon
          ? icon
          : <span className="km-railbox__letters">{glyph}</span>)}
      </button>

      {tip}
    </div>
  );
};

export default RailBox;
